import { createClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Client Supabase DÉDIÉ et ISOLÉ à la finalisation d'invitation.
//
// Pourquoi un client séparé du client principal de l'app ?
//  - Les liens d'invitation / réinitialisation arrivent avec les jetons dans le
//    fragment d'URL (#access_token=…&refresh_token=…). Le client principal est en
//    flowType 'pkce' (qui n'échange que les ?code=…) et possède un verrou no-op +
//    de l'auto-refresh + les écouteurs de l'AuthContext : dans cette configuration,
//    setSession() ne se résolvait pas de façon fiable sur cette page.
//  - Ce client n'a pas besoin de persister quoi que ce soit : il établit la session
//    en mémoire le temps de poser le mot de passe, puis on renvoie l'utilisateur
//    vers la page de connexion classique.
const inviteClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

export function DefinirMotDePassePage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const etablirSession = async () => {
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const accessToken = fragment.get('access_token');
      const refreshToken = fragment.get('refresh_token');
      const code = new URLSearchParams(window.location.search).get('code');
      try {
        if (accessToken && refreshToken) {
          // Lien implicite (cas des invitations / réinitialisations Supabase).
          const { data, error: e } = await inviteClient.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (!e && data.session) setHasSession(true);
        } else if (code) {
          // Lien PKCE (?code=…) — pris en charge par sécurité.
          const { data, error: e } = await inviteClient.auth.exchangeCodeForSession(code);
          if (!e && data.session) setHasSession(true);
        }
      } catch {
        // hasSession reste false → message « lien invalide ».
      }
      // Retire les jetons de la barre d'adresse (sécurité + évite la réutilisation).
      window.history.replaceState({}, '', window.location.pathname);
      setReady(true);
    };
    void etablirSession();
  }, []);

  const submit = () => {
    setError(null);
    if (pwd.length < 8) {
      setError('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    if (pwd !== pwd2) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setSaving(true);
    void inviteClient.auth.updateUser({ password: pwd }).then(({ error: e }) => {
      setSaving(false);
      if (e) {
        setError(e.message);
        return;
      }
      // Mot de passe posé : on libère le client isolé et on affiche l'écran de
      // succès qui renvoie vers la connexion classique.
      void inviteClient.auth.signOut();
      setDone(true);
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-t-4 border-t-action bg-card p-8">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <svg viewBox="0 0 32 32" aria-hidden="true" className="h-9 w-9">
              <path
                d="M6 18 L16 8 L26 18 L23 18 L23 26 L19 26 L19 20 L13 20 L13 26 L9 26 L9 18 Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">IMP TOITURE CRM</h1>
          <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-action" />
          <p className="mt-1 text-sm text-muted-foreground">Première connexion</p>
        </div>

        {done ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Votre mot de passe a bien été créé. Vous pouvez maintenant vous connecter.
            </p>
            <Button
              className="w-full"
              onClick={() => {
                navigate('/login');
              }}
            >
              Aller à la connexion
            </Button>
          </div>
        ) : !ready ? (
          <p className="text-sm text-muted-foreground">Vérification du lien…</p>
        ) : !hasSession ? (
          <div className="space-y-3">
            <p className="text-sm text-destructive">
              Lien invalide ou expiré. Demandez une nouvelle invitation à votre administrateur.
            </p>
            <Link to="/login" className="text-sm text-primary underline">
              Retour à la connexion
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choisissez votre mot de passe pour accéder à votre espace.
            </p>
            <div className="space-y-1">
              <Label htmlFor="pwd">Mot de passe</Label>
              <Input
                id="pwd"
                type="password"
                value={pwd}
                onChange={(e) => {
                  setPwd(e.target.value);
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pwd2">Confirmer</Label>
              <Input
                id="pwd2"
                type="password"
                value={pwd2}
                onChange={(e) => {
                  setPwd2(e.target.value);
                }}
              />
            </div>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <Button className="w-full" disabled={saving} onClick={submit}>
              {saving ? 'Enregistrement…' : 'Créer mon mot de passe'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
